<template>
  <v-overlay v-if="isFetchingInterviewSlot" z-index="10">
    <v-progress-circular indeterminate size="64" />
  </v-overlay>
  <div
    class="interview-details container col-lg-8 col-sm-12"
    v-else-if="currentInterviewSlot"
  >
    <div class="d-flex flex-column align-center">
      <PrimaryButton text shrink @click.stop="handleBack">
        <span> Interviews </span>
        <template v-slot:prepend:icon>
          <v-icon size="1.2rem" class="ml-2">
            mdi-chevron-left
          </v-icon>
        </template>
      </PrimaryButton>
    </div>
    <v-divider />
    <div>
      <div class="d-flex flex-column align-center justify-center">
        <v-icon class="interview-icon" large>
          mdi-clipboard-text-outline
        </v-icon>
        <div class="d-flex" :class="{ 'flex-column': isMobile }">
          <Permissioned section="interviews.view" class="no-overflow" vertical>
            <h1 permission="name" class="mr-2">
              {{ interviewName }}
            </h1>
          </Permissioned>
          <Chip
            color="red"
            class="full-width font-weight-bold"
            shrink
            v-if="currentInterviewSlot.cancelled"
          >
            CANCELLED
          </Chip>
        </div>
        <Permissioned section="interviews.view">
          <span
            class="secondary-text"
            :class="{ strikethrough: currentInterviewSlot.cancelled }"
            permission="datetime"
          >
            {{ interviewDateTimeRange }}
          </span>
        </Permissioned>
      </div>
      <Permissioned vertical section="interviews.view">
        <Comment
          v-if="size(currentInterviewSlot.comments)"
          permission="comments"
          :comments="currentInterviewSlot.comments"
          readOnly
          :minimizable="false"
        />
        <div permission="location" v-if="location">
          <h2 class="font-weight-bold mt-5 mb-3">
            Interview Location
          </h2>
          <a :href="locationMapsLink">{{ location }}</a>
        </div>
        <div permission="interview-participants">
          <h2 class="mt-5 mb-1">Participants</h2>
          <InterviewParticipants
            :participants="interviewers"
            participantsType="interviewers"
          />
          <InterviewParticipants
            :participants="candidates"
            participantsType="candidates"
          />
        </div>
        <h2 class="pt-3">
          Details
        </h2>
        <h3 class="mt-4 mb-2">Description</h3>
        <DescriptionTabs
          :descriptions="descriptions"
          class="full-width"
          isHTML
        />
        <v-skeleton-loader v-if="isFetchingFiles" type="list-item" />
        <h3 class="pt-3">
          Additional Details
        </h3>
        <DescriptionTabs
          :descriptions="additionalDescriptions"
          class="full-width"
          isHTML
        />
        <InterviewFeedbackSection
          v-if="size(currentInterviewSlot.feedbacks)"
          permission="feedback"
          :feedbacks="currentInterviewSlot.feedbacks"
        />
      </Permissioned>
    </div>
    <BaseFooter v-if="!isFetchingInterviewSlot">
      <ExpandedButtons>
        <Permissioned
          :vertical="isMobile"
          section="interviews"
          class="actions-bar"
        >
          <SubmitFeedbackAction permission="submit-feedback" />
          <RequestRescheduleInterviewAction permission="request-reschedule" />
          <RescheduleInterviewAction permission="reschedule-interview" />
          <CancelInterviewAction permission="cancel" />
          <ConfirmInterviewAction permission="confirm-interview" />
          <DeclineInterviewAction permission="decline-interview" />
          <ConfirmAdjustmentAction
            :candidate="first(candidates)"
            permission="confirm-adjustment"
          />
        </Permissioned>
      </ExpandedButtons>
    </BaseFooter>
  </div>
</template>

<script>
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import InterviewParticipants from "@/views/bookings/BookingInterviews/components/InterviewParticipants";
import CancelInterviewAction from "@/views/interviews/actions/CancelInterviewAction";
import DeclineInterviewAction from "@/views/interviews/actions/DeclineInterviewAction";
import ConfirmInterviewAction from "@/views/interviews/actions/ConfirmInterviewAction";
import ConfirmAdjustmentAction from "@/views/interviews/actions/ConfirmAdjustmentAction";
import RequestRescheduleInterviewAction from "@/views/interviews/actions/RequestRescheduleInterviewAction";
import InterviewFeedbackSection from "@/views/interviews/components/InterviewFeedbackSection";
import SubmitFeedbackAction from "@/views/interviews/actions/SubmitFeedbackAction";
import RescheduleInterviewAction from "@/views/interviews/actions/RescheduleInterviewAction";
import pluralize from "pluralize";
import { size, startCase, first } from "lodash";
import DescriptionTabs from "@/views/bookings/components/DescriptionTabs";
import BaseFooter from "@/components/common/BaseFooter";
import { copyState } from "@/utils/helpers";
import { createNamespacedHelpers } from "vuex";
import { INTERVIEWS_NAMESPACE } from "@/store/modules/bookings/interviews";
import {
  FETCH_INTERVIEW_SLOT,
  FETCH_INTERVIEW_FORMAT_FILES
} from "@/store/modules/bookings/interviews/actions";
import {
  getInterviewParticipants,
  getInterviewDateTimeRange
} from "@/views/bookings/BookingInterviews/modules/utils";
import Permissioned from "@/components/common/Permissioned";
import { getParentRoutePath } from "@/router/utils";
import Chip from "@/components/common/Chip";
import Comment from "@/components/common/Comment";
import ExpandedButtons from "@/components/common/ExpandedButtons";
import { isPermissioned } from "@/utils/permissions";

const { mapActions, mapState } = createNamespacedHelpers(INTERVIEWS_NAMESPACE);

export default {
  name: "InterviewDetails",
  components: {
    CancelInterviewAction,
    RequestRescheduleInterviewAction,
    SubmitFeedbackAction,
    RescheduleInterviewAction,
    PrimaryButton,
    InterviewParticipants,
    DescriptionTabs,
    BaseFooter,
    InterviewFeedbackSection,
    Permissioned,
    ConfirmInterviewAction,
    DeclineInterviewAction,
    ConfirmAdjustmentAction,
    Chip,
    Comment,
    ExpandedButtons
  },
  async created() {
    await this.fetchInterviewSlot(this.currentInterviewId);
    if (!this.currentInterviewSlot) {
      return this.$router.push("/pageNotFound");
    }
    this.fetchFiles(this.currentInterviewSlot.interviewFormat.id);
    document.title = `${this.interviewName} - Interviews - Track`;
  },
  data() {
    return {
      interviewFormatInternalFiles: null,
      interviewFormatExternalFiles: null,
      isFetchingFiles: false
    };
  },
  computed: {
    ...mapState(copyState(["currentInterviewSlot", "isFetchingInterviewSlot"])),
    currentInterviewId() {
      return this.$route.params.interviewId;
    },
    interviewName() {
      return startCase(this.currentInterviewSlot.interviewFormat.interviewName);
    },
    location() {
      if (!this.currentInterviewSlot.location) {
        return null;
      }
      const { line1, line2, line3 } = this.currentInterviewSlot.location;

      return [line1, line2, line3].filter(Boolean).join(", ");
    },
    locationMapsLink() {
      if (!this.currentInterviewSlot.location) {
        return null;
      }
      const { line1, line2, line3 } = this.currentInterviewSlot.location;
      const mapURL = "https://www.google.com/maps?q=";
      const stringConcat = [line1, line2, line3].filter(Boolean).join(", ");
      const mapQuery = stringConcat.replace(/ /g, "+");
      return mapURL.concat(mapQuery);
    },
    interviewers() {
      return getInterviewParticipants(this.currentInterviewSlot, "interviewer");
    },
    candidates() {
      return getInterviewParticipants(this.currentInterviewSlot, "candidate");
    },
    interviewDateTimeRange() {
      return getInterviewDateTimeRange(this.currentInterviewSlot);
    },
    additionalDescriptions() {
      const {
        interviewerAdditionalDetails,
        candidateAdditionalDetails
      } = this.currentInterviewSlot;
      return [
        candidateAdditionalDetails && {
          label: "For Candidates",
          value: candidateAdditionalDetails
        },
        interviewerAdditionalDetails && {
          label: "For Interviewers",
          value: interviewerAdditionalDetails
        }
      ].filter(Boolean);
    },
    descriptions() {
      const {
        candidateDetails,
        interviewerDetails
      } = this.currentInterviewSlot.interviewFormat;
      return [
        this.showCandidateDescription &&
          candidateDetails && {
            label: "For Candidates",
            value: candidateDetails,
            files: this.interviewFormatExternalFiles
          },
        this.showInterviewerDescription &&
          interviewerDetails && {
            label: "For Interviewers",
            value: interviewerDetails,
            files: this.interviewFormatInternalFiles
          }
      ].filter(Boolean);
    },
    showCandidateDescription() {
      return isPermissioned("interviews.view.candidate-description");
    },
    showInterviewerDescription() {
      return isPermissioned("interviews.view.interviewer-description");
    }
  },
  methods: {
    ...mapActions({
      fetchInterviewSlot: FETCH_INTERVIEW_SLOT,
      fetchInterviewFormatFiles: FETCH_INTERVIEW_FORMAT_FILES
    }),
    handleBack() {
      const path = this.$router.currentRoute.path;
      this.$router.push(getParentRoutePath(path));
    },
    async fetchFiles(interviewFormatId) {
      this.isFetchingFiles = true;
      if (this.showInterviewerDescription) {
        this.interviewFormatInternalFiles = await this.fetchInterviewFormatFiles(
          {
            interviewFormatId,
            collection: "internal"
          }
        );
      }
      if (this.showCandidateDescription) {
        this.interviewFormatExternalFiles = await this.fetchInterviewFormatFiles(
          {
            interviewFormatId,
            collection: "external"
          }
        );
      }
      this.isFetchingFiles = false;
    },
    pluralize,
    size,
    first
  }
};
</script>

<style lang="scss">
.interview-details {
  padding-bottom: 25px;

  .interview-icon {
    background-color: rgba($river-bed, 0.15);
  }

  .location-text {
    font-weight: bold;
    color: $primary-darker;
  }

  .no-overflow {
    &.permissioned-container {
      overflow: hidden !important;
    }
  }
}
</style>
